import { unwrapResult } from "@reduxjs/toolkit"
import { ReactElement, useCallback, useEffect, useState } from "react"
import { BlobMeta, loadBlobs, selectBlobsByResourceId, uploadBlobs } from "../features/documents/attachementsSlice"
import { useAppDispatch, useAppSelector } from "./hooks"

export interface BlobsProps {
    guid: string
    blobs: BlobMeta[]
    upload: (files: File[]) => Promise<void>
    uploading: boolean
}

export interface AppBlobsProviderProps<T> {
    guid: string
    /** Object metadata persisted on uploaded blobs (see {@link BlobResourceType}). */
    resourceType?: string
    parentResourceId?: string
    parentResourceType?: string
    component: (props: T & BlobsProps) => ReactElement
    args : T
}

export function AppBlobsProvider<T>(props: AppBlobsProviderProps<T>) {
    const { guid, args, resourceType, parentResourceId, parentResourceType } = props
    const dispatch = useAppDispatch()

    const blobs = useAppSelector(s => selectBlobsByResourceId(s, guid))

    const [loading, setLoading] = useState<boolean>(false)
    const [uploading, setUploading] = useState<boolean>(false)

    const upload = useCallback((files: File[]) => {
        setUploading(true)
        return dispatch(uploadBlobs([guid, files, { resourceType, parentResourceId, parentResourceType }]))
            .then(x => {
                unwrapResult(x)
                return
            })
            .catch(err => console.error('Application error: ', err))
            .finally(() => setUploading(false))
    }, [guid, resourceType, parentResourceId, parentResourceType])

    useEffect(() => {
        if (guid && blobs === undefined) {
            setLoading(true)
            dispatch(loadBlobs(guid))
                .then(unwrapResult)
                .finally(() => setLoading(false))
        }
    }, [guid, blobs])

    const Component = props.component

    if (!guid) return null

    if (blobs === undefined) {
        if (loading) {
            return <>Loading blobs...</>
        } else {
            return <>Loading completed but blobs still undefined!🔥🔥🔥🔥🔥</>
        }
    } else {
        return <Component {...args} blobs={blobs.blobs ?? []} upload={upload} uploading={uploading} guid={guid} />
    }
}
